/**
 * The onboarding and sign-in surfaces are a deliberately fixed near-black
 * canvas rather than the themed editor chrome, so the palette lives here as
 * class fragments instead of reaching for the app's light/dark tokens.
 */

export const SURFACE_BG = "bg-[#0a0a0a]";

/**
 * Focus ring.
 *
 * A hard white ring with a dark offset gap read as a stray border drawn around
 * the control, especially over artwork: two hard edges where the design has
 * none. This is a soft halo instead, sitting directly on the control with no
 * offset gap, so focus is unmistakable without looking like a mistake.
 *
 * Keyboard focus only. A mouse click should not paint a ring at all, which is
 * what focus-visible buys.
 */
export const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/45";

/**
 * Control geometry shared by chips, inputs and the Back/Continue buttons.
 * The 10px corner is the platform radius (--radius: 0.625rem in index.css,
 * and the same 10px the landing page's .btn uses). These were fully rounded
 * pills, which read as a different product sitting next to the site.
 */
export const PILL_BASE =
  "inline-flex h-11 shrink-0 items-center justify-center gap-2.5 rounded-[10px] border-0 px-5 text-[15px] font-medium";

/**
 * Flat hover: colour only, never a lift. Elevation is a background-value step,
 * not a translate and no longer a border. Suppressed under reduced motion.
 */
export const PILL_MOTION =
  "motion-safe:transition-[background-color,color] motion-safe:duration-200";

export const PILL_LIGHT = "bg-white text-[#0a0a0a] hover:bg-white/90";

export const PILL_DARK =
  "bg-white/[0.06] text-[#a8a8a8] hover:bg-white/[0.12] hover:text-white";

export const PILL_DIMMED = "cursor-not-allowed bg-white/[0.06] text-white/30";

/**
 * Text fields, borderless.
 *
 * A hairline box around every input reads as a form; on a surface that is
 * mostly artwork it also fights the picture. The field is a soft translucent
 * slab instead, with no border at all. Focus is shown by the slab lifting and
 * a ring, never by a line appearing, so nothing shifts by a pixel on focus.
 */
export const FIELD_BASE =
  "w-full rounded-[12px] border-0 bg-white/[0.06] px-5 py-3.5 text-left backdrop-blur-sm";

export const FIELD_MOTION =
  "motion-safe:transition-[background-color,box-shadow] motion-safe:duration-200";

/** Focus-within for a label wrapping an input; focus-visible for a bare one. */
export const FIELD_FOCUS =
  "focus-within:bg-white/[0.1] focus-within:ring-2 focus-within:ring-white/45";

/** The input itself: no chrome, the slab around it does the work. */
export const FIELD_INPUT =
  "w-full min-w-0 border-0 bg-transparent text-[15px] font-medium tracking-[0.01em] text-white outline-none " +
  "placeholder:font-light placeholder:tracking-[0.02em] placeholder:text-white/25";

/**
 * Cards on this surface: no border, no lift, no scale. Elevation is the same
 * background step the pills use, one value brighter on hover.
 */
export const CARD_BASE =
  "group relative flex flex-col overflow-hidden rounded-[14px] border-0 bg-white/[0.05] text-left " +
  "motion-safe:transition-[background-color] motion-safe:duration-200 hover:bg-white/[0.09]";

/** Strip headings. Deliberately quiet: the hero is the only loud type here. */
export const SECTION_HEAD =
  "text-[15px] font-normal tracking-[-0.01em] text-white";

/** The one muted grey the surface uses for secondary copy. */
export const MUTED = "text-[#8f8f8f]";
